import Drawer from '@mui/material/Drawer';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import PonduranceLogoFull from '@/assets/pondurance-horizontal-full.svg';
import PonduranceLogoMini from '@/assets/pondurance-horizontal-mini.svg';
import OrganizationDropdown from '@/components/OrganizationDropdown';
import { SIDEBAR_MENU_ITEMS } from '@/constants/sidebar.constants';
import { parseSearchFiltersToQueryParams } from '@/features/SearchBar';
import { assetsFiltersAtom, assetsTimeFilterAtom } from '@/pages/Assets';
import { organizationsAtom } from '@/pages/Organizations';
import { isLayoutMobileAtom } from '@/store/layout';
import { isSidebarExpandedAtom, isSidebarOpenAtom } from '@/store/sidebar';
import userAtom from '@/store/user.store';

import useStyles from './Sidebar.styles';
import { SidebarContact } from './SidebarContact';
import { SidebarMenu } from './SidebarMenu';

function Sidebar() {
  const [assetsFilters] = useAtom(assetsFiltersAtom);
  const [assetsTimeFilter] = useAtom(assetsTimeFilterAtom);
  const [organizations] = useAtom(organizationsAtom);
  const [isLayoutMobile] = useAtom(isLayoutMobileAtom);
  const [isSidebarExpanded, setIsSidebarExpanded] = useAtom(
    isSidebarExpandedAtom
  );
  const [isSidebarOpen, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);
  const [user] = useAtom(userAtom);
  const { classes } = useStyles({
    isLayoutMobile,
    isSidebarExpanded,
    organizationsLengthEqualToOne: organizations.length === 1
  });

  const setPathTo = (label: string, path?: string) => {
    if (label === 'Asset Inventory') {
      const assetsSearchParams = new URLSearchParams({
        t: assetsTimeFilter,
        q: parseSearchFiltersToQueryParams(assetsFilters)
      });
      return `/asset-inventory?${assetsSearchParams.toString()}`;
    }
    if (label === 'Tickets') {
      return `/tickets`;
    }
    if (label === 'Vulnerability Management') {
      return '/vulnerability-management/vulnerabilities';
    }
    return path;
  };

  const menuItems = SIDEBAR_MENU_ITEMS.map((item) => ({
    ...item,
    pathTo: setPathTo(item.label, item.pathTo)
  }));

  const handleClose = () => {
    if (isLayoutMobile) {
      setIsSidebarOpen(false);
      return;
    }
    setIsSidebarExpanded(false);
  };

  const logo = useMemo(() => {
    if (isLayoutMobile || isSidebarExpanded) {
      return PonduranceLogoFull;
    }
    return PonduranceLogoMini;
  }, [isLayoutMobile, isSidebarExpanded]);

  return (
    <Drawer
      anchor="left"
      className={classes.sidebar}
      open={isLayoutMobile ? isSidebarOpen : isSidebarExpanded}
      onClose={handleClose}
      variant={isLayoutMobile ? 'temporary' : 'permanent'}
    >
      <div className={classes.sidebarHeader}>
        <img alt="Pondurance Logo" className={classes.sidebarLogo} src={logo} />
      </div>
      {isLayoutMobile && organizations.length > 1 && (
        <OrganizationDropdown persistTheme="dark" />
      )}
      {user && <SidebarMenu menuItems={menuItems} />}
      <SidebarContact />
    </Drawer>
  );
}

export default Sidebar;
